import { useAuthContext } from "@asgardeo/auth-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getGroupValues, hasAllowedGroup } from "../../auth/groupClaims";

// Reads the allowed groups from the env var.
// Set VITE_PRO_PLAN_ALLOWED_GROUPS=professional-selected in your .env
function getAllowedGroups() {
    const raw = import.meta.env.VITE_PRO_PLAN_ALLOWED_GROUPS || "";
    return raw
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean);
}

function NotSelectedPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen flex items-center justify-center bg-pl-bg px-4 py-8">
            <div className="w-full max-w-md bg-pl-bg-elevated border border-pl-border rounded-xl p-6 sm:p-8 shadow-md">

                {/* Icon */}
                <div className="w-12 h-12 rounded-lg bg-pl-bg-subtle flex items-center justify-center mb-5 text-pl-text-tertiary">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                            fill="currentColor"
                        />
                    </svg>
                </div>

                {/* Heading */}
                <p className="text-lg sm:text-xl font-bold text-pl-text-primary mb-2">
                    paralegal.lk Professional Membership access is not active for this account.
                </p>

                {/* Description */}
                <p className="text-sm sm:text-base text-pl-text-secondary leading-relaxed mb-6">
                    Access to the paralegal.lk legal AI workspace is currently by application. Apply now and we will review your request within 3 working days.
                </p>

                <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                        type="button"
                        onClick={() => navigate("/apply-for-membership")}
                        className="inline-flex items-center justify-center rounded-lg bg-pl-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-pl-accent-hover"
                    >
                        Apply for legal workspace
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="inline-flex items-center justify-center rounded-lg border border-pl-border px-5 py-2.5 text-sm font-semibold text-pl-text-secondary transition-colors duration-150 hover:border-pl-border-strong hover:text-pl-text-primary"
                    >
                        Home
                    </button>
                </div>
            </div>
        </div>
    );
}

const ProfessionalPlanGuard = ({ children }) => {
    const { state, getDecodedIDToken } = useAuthContext();
    const [claims, setClaims] = useState(null);
    const [claimsLoading, setClaimsLoading] = useState(false);

    const groupAuthEnabled = import.meta.env.VITE_PRO_PLAN_GROUP_AUTH_ENABLED !== "false";
    if (!groupAuthEnabled) return children;

    // Fetch decoded ID token claims once the user is authenticated
    useEffect(() => {
        let isActive = true;

        if (!state.isAuthenticated) {
            setClaims(null);
            return undefined;
        }

        setClaimsLoading(true);
        getDecodedIDToken()
            .then((decodedClaims) => {
                if (isActive) setClaims(decodedClaims || {});
            })
            .catch(() => {
                if (isActive) setClaims({});
            })
            .finally(() => {
                if (isActive) setClaimsLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [getDecodedIDToken, state.isAuthenticated]);

    const allowedGroups = useMemo(() => getAllowedGroups(), []);

    const groups = useMemo(
        () =>
            getGroupValues({
                groupsClaimPath: import.meta.env.VITE_PRO_PLAN_GROUPS_CLAIM_PATH || "groups",
                idTokenClaims: claims,
                user: null,
            }),
        [claims]
    );

    const isAuthorized = hasAllowedGroup({ allowedGroups, groups });

    // Still loading auth state or fetching claims → render nothing (the parent
    // ProtectedProPlanRoute already shows nothing while loading)
    if (state.isLoading || claimsLoading || (state.isAuthenticated && claims === null)) {
        return null;
    }

    // Not in the allowed group → show the "not selected" page
    if (!isAuthorized) {
        return <NotSelectedPage />;
    }

    // Authorized — render the page
    return children;
};

export default ProfessionalPlanGuard;
